"use client";
import { useEffect, useState } from "react";
import ScreenLayout from "@/components/layout/screen-layout";
import { caseStudies } from "@/lib/dto/case-studies/case-studies";
import ServicePageMeta from "@/components/common/seo/service-page-meta";
import { BlogApi } from "@/app/network/apis/blog-api";

type SitemapItem = { name: string; href: string };
type SitemapMap = Record<string, SitemapItem[]>;

const groupByFirstLetter = (items: SitemapItem[]): SitemapMap =>
  items.reduce<SitemapMap>((grouped, item) => {
    const firstLetter = item.name.charAt(0).toUpperCase();
    if (!grouped[firstLetter]) grouped[firstLetter] = [];
    grouped[firstLetter].push(item);
    return grouped;
  }, {} as SitemapMap);

export default function SitemapPage() {
  // Extract case study links and categorize them alphabetically
  const caseStudyLinks: SitemapItem[] = caseStudies.map((cs) => ({
    name: `${cs.companyName} - Case Study`,
    href: cs.link,
  }));

  // Group case studies by first letter of company name
  const caseStudiesByLetter: SitemapMap = groupByFirstLetter(caseStudyLinks);

  const staticSitemapData: SitemapMap = {
    A: [
      { name: "About Us", href: "/about-us" },
      { name: "AI Engineering Squad", href: "/ai-engineering-squad" },
      ...(caseStudiesByLetter["A"] || []),
    ],
    B: [{ name: "Blogs", href: "/blogs" },
        ...(caseStudiesByLetter["B"] || []),
    ],
    C: [
      { name: "Careers", href: "/careers" },
      { name: "Case Studies", href: "/case-studies" },
      { name: "Contact Us", href: "/contact-us" },
      { name: "Customer Success", href: "/customer-success" },
      { name: "CRM Development Services", href: "/crm-development-services" },
      ...(caseStudiesByLetter["C"] || []),
    ],
    E: [
      ...(caseStudiesByLetter["E"] || []),
    ],
    F: [{ name: "FAQs", href: "/faqs" },
        ...(caseStudiesByLetter["F"] || []),
    ],
    G: [
      ...(caseStudiesByLetter["G"] || []),
      {
        name: "Gen-AI Development Services",
        href: "/gen-ai-development-services",
      },
    ],
    H: [{ name: "Home", href: "/" },
        ...(caseStudiesByLetter["H"] || []),
    ],
    L: [...(caseStudiesByLetter["L"] || [])],
    M: [
      {
        name: "Mobile App Development Services",
        href: "/mobile-app-development-services",
      },
      ...(caseStudiesByLetter["M"] || []),
    ],
    O: [
      { name: "Open Source", href: "/open-source" },
      ...(caseStudiesByLetter["O"] || []),
    ],
    P: [
      { name: "Privacy Policy", href: "/privacy-policy" },
      { name: "Product Engineering Services", href: "/product-engineering" },
      ...(caseStudiesByLetter["P"] || []),
    ],
    S: [{ name: "Sitemap", href: "/sitemap" },
        ...(caseStudiesByLetter["S"] || []),
    ],
    T: [{ name: "Tech Stack", href: "/tech-stack" },
        ...(caseStudiesByLetter["T"] || []),
    ],
    W: [
      { name: "Web Development Services", href: "/web-development-services" },
      ...(caseStudiesByLetter["W"] || []),
    ],
  };

  // Blogs loaded via BlogList API and grouped by first letter of title
  const [blogsByLetter, setBlogsByLetter] = useState<SitemapMap>({});

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        let allBlogPosts: any[] = [];
        let skip = 0;
        const limit = 100;
        let hasMore = true;

        while (hasMore) {
          const response = await BlogApi.getBlogList({ skip, limit });
          const blogs = response?.r?.blogs || [];

          allBlogPosts = allBlogPosts.concat(blogs);
          hasMore = response?.r?.pagination?.has_more || false;
          skip += limit;

        //avoid infinite loop
          if (skip > 1000) break;
        }

        const blogItems: SitemapItem[] = allBlogPosts
          .filter((blog) => blog?.slug && blog?.title && blog?.status === 1)
          .map((blog) => ({
            name: blog.title as string,
            href: `/blogs/${blog.slug}`,
          }));

        setBlogsByLetter(groupByFirstLetter(blogItems));
      } catch (error) {
        console.error("Error fetching blogs for sitemap:", error);
      }
    };

    void fetchBlogs();
  }, []);

  // Merge static pages + blogs with all letters appear
  const mergedSitemapData: SitemapMap = { ...staticSitemapData };

  for (const [letter, links] of Object.entries(blogsByLetter)) {
    if (!mergedSitemapData[letter]) {
      mergedSitemapData[letter] = [];
    }
    mergedSitemapData[letter].push(...links);
  }

  return (
    <>
      <ServicePageMeta
        title="Sitemap | Complete Site Structure & Navigation | Devkrest"
        description="Explore Devkrest's complete website structure with our comprehensive sitemap. Find all pages, services, case studies, and resources organized alphabetically for easy navigation. Discover our full range of software development services and solutions."
        keywords={[
          "sitemap",
          "site structure",
          "website navigation",
          "page directory",
          "site organization",
          "navigation guide",
          "website map",
          "page index",
          "site overview",
          "Devkrest sitemap",
          "website structure"
        ]}
        serviceType="Sitemap"
        url="/sitemap"
        image="/images/sitemap/sitemap-hero.webp"
      />
      <main className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative pt-40 md:pt-44 lg:pt-48 pb-20 overflow-hidden bg-gradient-to-br from-white via-purple-50/30 to-white">
        {/* Background Elements */}
        <div className="absolute inset-0 w-full h-full">
          <div className="absolute inset-0 bg-[linear-gradient(rgba(82,49,231,0.02)_1.6px,transparent_1.6px),linear-gradient(to_right,rgba(82,49,231,0.02)_1.6px,transparent_1.6px)] bg-[size:24px_24px]" />
          <div className="absolute top-20 left-10 w-20 h-20 bg-gradient-to-r from-purple-400/20 to-blue-400/20 rounded-full blur-xl opacity-50" />
          <div className="absolute bottom-20 right-10 w-32 h-32 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-full blur-xl opacity-50" />
        </div>

        {/* Content Container */}
        <div className="relative z-10">
          <ScreenLayout>
            <div className="max-w-4xl mx-auto">
              <div className="space-y-6 text-center">
                {/* Main Heading */}
                <div className="space-y-4">
                  <h1 className="text-4xl sm:text-5xl md:text-6xl font-semibold leading-[1.1]">
                    <span className="text-gray-900">Site</span>{" "}
                    <span className="bg-gradient-to-r from-[#5231E7] to-[#764AF1] bg-clip-text text-transparent">
                      Map
                    </span>
                  </h1>
                </div>

                {/* Subtitle */}
                <div className="space-y-4">
                  <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                    Navigate through all pages and sections of our website
                    organized alphabetically for easy access.
                  </p>
                </div>
              </div>
            </div>
          </ScreenLayout>
        </div>
      </div>

      {/* Content Section */}
      <div className="py-12 md:py-16">
        <ScreenLayout>
          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12">
              <div className="prose prose-lg max-w-none">
                {/* Introduction */}
                <p className="text-gray-700 leading-relaxed mb-8">
                  Explore our complete website structure organized
                  alphabetically. Find all pages, services, and resources in one
                  convenient location.
                </p>

                {/* Sitemap Categories */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {Object.entries(mergedSitemapData)
                    .filter(([, pages]) => pages.length > 0)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([letter, pages]) => (
                    <div
                      key={letter}
                      className="border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow duration-300"
                    >
                      <div className="flex items-center mb-4">
                        <span className="w-8 h-8 bg-gradient-to-r from-[#5231E7] to-[#764AF1] text-white rounded-[50%] flex items-center justify-center text-sm font-bold mr-3">
                          {letter}
                        </span>
                        <h2 className="text-xl font-semibold text-gray-900">
                          {letter}
                        </h2>
                      </div>
                      <div className="space-y-2">
                        {pages.map((page, index) => (
                          <div key={index} className="pl-11">
                            <a
                              href={page.href}
                              className="text-gray-600 hover:text-[#5231E7] transition-colors duration-200 block py-1"
                            >
                              {page.name}
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Additional Information */}
                <div className="mt-16 pt-8 border-t border-gray-200">
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                    Need help finding something?
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    Can't find what you're looking for? Our team is here to help
                    you navigate our website and find the information you need.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Contact us at:{" "}
                    <a
                      href="mailto:hello@devkrest.com"
                      className="text-[#5231E7] hover:text-[#764AF1] font-medium transition-colors duration-200"
                    >
                      hello@devkrest.com
                    </a>{" "}
                    or visit our{" "}
                    <a
                      href="/contact-us"
                      className="text-[#5231E7] hover:text-[#764AF1] font-medium transition-colors duration-200"
                    >
                      contact page
                    </a>{" "}
                    for assistance.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </ScreenLayout>
      </div>
    </main>
    </>
  );
}
